import React from 'react';
import { Link } from 'react-router-dom';
import ReactFlow, { useNodesState, useEdgesState, addEdge, Handle, Position, Background, Controls, MiniMap, useViewport, NodeResizer } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Workflow,
  Building2,
  Users,
  Shield,
  MessageCircle,
  Wallet,
  CheckSquare,
  BarChart3,
  Clock,
  Layers,
  Handshake,
  PiggyBank,
  FileText,
  FolderKanban,
  MapPin,
  PlayCircle,
} from 'lucide-react';

const processCards = [
  {
    id: 'vendor-management',
    title: 'Vendor Management Lifecycle',
    icon: Handshake,
    route: '/vendors',
    summary:
      'Maintain supplier records, approval readiness, and continuity of vendor-linked finance workflows.',
    intro:
      'This process is one of the most important foundations in the system, because invoices and purchasing quality depend on clean vendor data. For recording, narrate how a vendor enters the system, where it is reviewed, and how it becomes usable by downstream finance operations.',
    steps: [
      {
        action: 'Open vendors workspace',
        clickPath: 'Sidebar > Action Items > Vendors',
        destination: '/vendors',
        detail: 'This opens the main vendor list where admins can review status and maintain master data.',
      },
      {
        action: 'Review vendor details and status',
        clickPath: 'Vendors list > Select vendor row',
        destination: '/vendors (details in-page)',
        detail: 'Use this view to validate key fields and ensure the supplier is operationally ready.',
      },
      {
        action: 'Validate subscription-linked vendor services',
        clickPath: 'Sidebar > Finance > Subscriptions',
        destination: '/subscriptions',
        detail: 'Confirms recurring service vendors are aligned with current commercial terms.',
      },
      {
        action: 'Return to approvals queue context',
        clickPath: 'Sidebar > Action Items > Invoice Approvals',
        destination: '/approvals',
        detail: 'Shows how strong vendor governance improves approval speed and reduces exceptions.',
      },
    ],
  },
  {
    id: 'invoice-management',
    title: 'Invoice Management End-To-End',
    icon: FileText,
    route: '/invoices',
    summary:
      'Manage invoices from intake and OCR upload through review, approval, and posting.',
    intro:
      'This is the primary process many users care about most. The recording should demonstrate the complete journey: where invoices are created or uploaded, how users review detail, and how approved items move into posting.',
    steps: [
      {
        action: 'Open invoice list',
        clickPath: 'Sidebar > Finance > Invoices',
        destination: '/invoices',
        detail: 'The list is the operational inbox for invoice review, status tracking, and follow-up actions.',
      },
      {
        action: 'Upload new invoice batch',
        clickPath: 'From invoice area > Upload action',
        destination: '/invoices/upload',
        detail: 'Supports document intake and OCR-enabled extraction for structured processing.',
      },
      {
        action: 'Open invoice detail review',
        clickPath: 'Invoices list > Open invoice record',
        destination: '/invoices/:id',
        detail: 'Validates coding, supplier details, amounts, and readiness for approval routing.',
      },
      {
        action: 'Process approvals and finance posting',
        clickPath: 'Sidebar > Action Items > Invoice Approvals, then Post to Finance',
        destination: '/approvals, /invoice-posting',
        detail: 'Completes the decision and posting path so approved invoices reach finance systems.',
      },
    ],
  },
  {
    id: 'expense-management',
    title: 'Expense Management Journey',
    icon: Wallet,
    route: '/expenses',
    summary:
      'Guide users from expense submission through manager/finance approval and reporting visibility.',
    intro:
      'Expense processing is a high-frequency employee workflow, so videos should be very explicit. Show where a user submits an expense, where approvers review, and where finance obtains analytics and compliance visibility.',
    steps: [
      {
        action: 'Open My Expenses',
        clickPath: 'Sidebar > My Expenses > My Expenses',
        destination: '/expenses',
        detail: 'Users can see personal claims, statuses, and action-required items.',
      },
      {
        action: 'Submit new expense',
        clickPath: 'Sidebar > My Expenses > Submit Expense',
        destination: '/expenses/submit',
        detail: 'Captures a new claim and sends it into the approval process.',
      },
      {
        action: 'Run approval workflow',
        clickPath: 'Sidebar > Action Items > Expense Approvals',
        destination: '/expense-approvals',
        detail: 'Approvers review and decide claims, moving them toward final accounting outcomes.',
      },
      {
        action: 'Review claims analytics',
        clickPath: 'Sidebar > Reports > VAT Claims / Finance Dashboard',
        destination: '/vat-claims, /finance-dashboard',
        detail: 'Enables reporting, compliance tracking, and trend analysis after decisioning.',
      },
    ],
  },
  {
    id: 'budget-management',
    title: 'Budget Management And Control',
    icon: PiggyBank,
    route: '/manage-budget',
    summary:
      'Run the full annual budget cycle from Draft planning to Active consumption, year-end close, and next-year carry-forward.',
    intro:
      'This workflow follows real operating behavior in your app. A new financial year starts with Finance Admin creating budget headers in Draft status. Department Managers then fill detailed budget lines for their departments, Finance Admin validates and activates the budget, and approvers consume budget through invoice coding. During the year, amounts are recalculated continuously (Budgeted, Spent, Committed, Remaining). At year-end, the budget is reviewed and closed, and teams can import prior-year lines into the next FY and adjust line by line to match actual expected costs.',
    steps: [
      {
        action: 'Start new FY budget in Draft (Finance Admin)',
        clickPath: 'Sidebar > Budgets & Projects > Budgets > Create Budget Header',
        destination: '/budget-headers',
        detail: 'Create the budget header for the department and FY with Budget Name, Budget Type, Financial Year, Department, Total Budget, and Status=Draft. This is the planning container before any line-level allocation is consumed.',
      },
      {
        action: 'Department Manager adds detailed budget lines',
        clickPath: 'Budget Header row > View Lines > Add Line',
        destination: '/budget-lines?budgetId=...',
        detail: 'Each line is captured with actual app fields: GL Account (glAccountId), Description, Budgeted Amount, Department, Project, Cost Centre, Line Type (one_off/new_service/continuity), Spread Months, Spread Start Date, optional Spread Data monthly plan, and optional Subscription link. Managers can also import lines from template/Excel for faster setup.',
      },
      {
        action: 'Validate and activate budget (Finance Admin)',
        clickPath: 'Budget Header > Status action (submit/activate)',
        destination: '/budget-headers, /api/budgets/:id/submit',
        detail: 'After line checks (completeness, realistic allocation, and no over-allocation), budget moves Draft -> Active. Active budgets become the live control layer used by approval and coding flows.',
      },
      {
        action: 'Consume budget during invoice approvals and coding',
        clickPath: 'Sidebar > Action Items > Invoice Approvals > invoice line coding',
        destination: '/approvals, /invoices/:id',
        detail: 'Approvers assign each invoice line to the correct GL and budget line for the target department. Allocation records reduce available balance and enforce governance during operational processing.',
      },
      {
        action: 'Monitor instant recalculation and variance',
        clickPath: 'Sidebar > Budgets & Projects > Budget Analytics / Monthly View / Manage Budget',
        destination: '/budget-analytics, /budget-monthly-view, /manage-budget',
        detail: 'Budget values recalculate continuously as transactions move: BudgetedAmount, SpentAmount, CommittedAmount, RemainingAmount. Teams monitor overspend risk, trends, and department performance in near real-time.',
      },
      {
        action: 'Year-end review and carry missed items to next FY',
        clickPath: 'Budget analytics + lines review, then open next FY budget and import lines',
        destination: '/budget-analytics, /budget-lines, /budget-headers',
        detail: 'At end of FY, review actuals and identify missing or one-off items for the new year. Managers can import prior lines into the new FY budget and adjust line-by-line to reflect real expected costs instead of copying unchanged assumptions.',
      },
      {
        action: 'Close prior-year budget (operational archive)',
        clickPath: 'Budget status action > Close',
        destination: '/budget-headers, /api/budgets/:id/close',
        detail: 'Finance Admin closes the completed FY budget (Active/Locked -> Closed). Closed budgets are preserved for historical reporting and audit traceability while planning continues in the new FY.',
      },
    ],
  },
  {
    id: 'department-management',
    title: 'Department Management And User Allocation',
    icon: FolderKanban,
    route: '/departments',
    summary:
      'Create department structure, allocate users, and align approval/accountability boundaries.',
    intro:
      'Departments define operational ownership, so this process has direct impact on approvals and reporting quality. For video guides, explain how structural setup controls who can approve, who can view, and who is responsible for spend lines.',
    steps: [
      {
        action: 'Open departments list',
        clickPath: 'Sidebar > Administration > Departments',
        destination: '/departments',
        detail: 'Manage the organizational structure used for workflows and access segmentation.',
      },
      {
        action: 'Open user-department mapping',
        clickPath: 'Sidebar > Administration > User Departments',
        destination: '/user-departments',
        detail: 'Assign users to departments so ownership and routing rules become effective.',
      },
      {
        action: 'Open role permission controls',
        clickPath: 'Sidebar > Administration > Role Permissions',
        destination: '/role-permissions',
        detail: 'Ensure each department-aligned role has the minimum required capabilities.',
      },
      {
        action: 'Validate behavior in approval queues',
        clickPath: 'Sidebar > Action Items > Invoice Approvals / Expense Approvals',
        destination: '/approvals, /expense-approvals',
        detail: 'Confirms department setup produces correct routing and decision visibility in real workflows.',
      },
    ],
  },
  {
    id: 'company-onboarding',
    title: 'Company Onboarding And Initial Configuration',
    icon: Building2,
    route: '/companies',
    summary:
      'Create a new company, configure its core settings, and prepare it for user access and module rollout.',
    intro:
      'This process is used whenever a new customer company joins the platform. The objective is to create the company record, set up operating defaults, and verify that the company is ready for live users. For a screen recording, focus on the handoff moment: from platform ownership into company readiness.',
    steps: [
      {
        action: 'Open Companies list',
        clickPath: 'Sidebar > Platform > Companies',
        destination: '/companies',
        detail: 'This opens the master registry of all companies managed by the platform admin.',
      },
      {
        action: 'Open the company profile',
        clickPath: 'Companies table > Select company row',
        destination: '/companies/:id',
        detail: 'The company detail view is the operational control point for setup checks and lifecycle management.',
      },
      {
        action: 'Open company-side configuration',
        clickPath: 'Sidebar > Administration > Company Settings (inside target company context)',
        destination: '/company-settings',
        detail: 'Use this area to validate finance and operational settings before end users begin transactions.',
      },
      {
        action: 'Verify access and module readiness',
        clickPath: 'Sidebar > Platform > Platform Settings / OCR Pool & Storage / Subscriptions & Usage',
        destination: '/settings, /platform/resources, /license-status',
        detail: 'Confirm that integrations, quotas, and subscription posture support expected workload.',
      },
    ],
  },
  {
    id: 'identity-and-access',
    title: 'Identity, Roles, And Access Governance',
    icon: Users,
    route: '/users',
    summary:
      'Provision user access, assign role permissions, and maintain least-privilege governance at scale.',
    intro:
      'This process ensures the right users have the right capabilities. During recording, explain the security story: user creation, department mapping, permission tuning, and traceability through logs.',
    steps: [
      {
        action: 'Open user administration',
        clickPath: 'Sidebar > Platform > Platform Users',
        destination: '/users',
        detail: 'This is the entry point for managing identities across the platform footprint.',
      },
      {
        action: 'Map users to departments',
        clickPath: 'Sidebar > Administration > User Departments',
        destination: '/user-departments',
        detail: 'Defines operational ownership and approval boundaries within company structures.',
      },
      {
        action: 'Tune permission model',
        clickPath: 'Sidebar > Administration > Role Permissions',
        destination: '/role-permissions',
        detail: 'Adjust role capabilities and validate that sensitive actions remain restricted.',
      },
      {
        action: 'Review access trail',
        clickPath: 'Sidebar > Monitoring > Audit Logs (All Companies)',
        destination: '/audit-logs',
        detail: 'Demonstrates who changed what and when, enabling compliance and forensic review.',
      },
    ],
  },
  {
    id: 'approval-operations',
    title: 'Approval Operations (Invoices, Expenses, Reconciliation)',
    icon: CheckSquare,
    route: '/approvals',
    summary:
      'Operate high-volume approval queues and clear exceptions into posted finance outcomes.',
    intro:
      'This process is ideal for demonstrating daily platform operations. The story is queue intake, decisioning, exception resolution, and posting. For YouTube guidance, narrate status changes and where each decision appears next.',
    steps: [
      {
        action: 'Open invoice approvals queue',
        clickPath: 'Sidebar > Action Items > Invoice Approvals',
        destination: '/approvals',
        detail: 'Shows pending invoice tasks and supports approve/reject workflows.',
      },
      {
        action: 'Open expense approvals queue',
        clickPath: 'Sidebar > Action Items > Expense Approvals',
        destination: '/expense-approvals',
        detail: 'Handles employee expense decisions and routing outcomes.',
      },
      {
        action: 'Open rate reconciliation',
        clickPath: 'Sidebar > Action Items > Rate Reconciliation',
        destination: '/exchange-rate-reconciliation',
        detail: 'Resolves exchange-related mismatches prior to downstream posting.',
      },
      {
        action: 'Post to finance',
        clickPath: 'Sidebar > Action Items > Post to Finance',
        destination: '/invoice-posting',
        detail: 'Moves approved and prepared records toward the accounting integration boundary.',
      },
    ],
  },
  {
    id: 'support-incident',
    title: 'Support Ticket And Incident Handling',
    icon: MessageCircle,
    route: '/support/tickets',
    summary:
      'Manage inbound support demand from ticket intake through triage, investigation, and closure.',
    intro:
      'Use this process to show service quality controls. The recording should explain priority, ownership, evidence gathering, and closure discipline. Tie incidents back to system logs and activity evidence to make the guidance practical.',
    steps: [
      {
        action: 'Open support queue',
        clickPath: 'Sidebar > Monitoring > Support Tickets',
        destination: '/support/tickets',
        detail: 'Displays open and historical tickets requiring platform-level attention.',
      },
      {
        action: 'Inspect related user activity',
        clickPath: 'Sidebar > Administration > User Activity',
        destination: '/user-activity',
        detail: 'Correlates ticket reports with concrete user actions for faster diagnosis.',
      },
      {
        action: 'Inspect audit timeline',
        clickPath: 'Sidebar > Monitoring > Audit Logs (All Companies)',
        destination: '/audit-logs',
        detail: 'Verifies configuration or permission changes linked to the issue.',
      },
      {
        action: 'Close the loop with ops checks',
        clickPath: 'Sidebar > Monitoring > OCR Pool & Storage / Subscriptions & Usage',
        destination: '/platform/resources, /license-status',
        detail: 'Validates whether incident symptoms are related to capacity, service health, or commercial limits.',
      },
    ],
  },
  {
    id: 'finance-observability',
    title: 'Finance Visibility And Performance Monitoring',
    icon: BarChart3,
    route: '/finance-dashboard',
    summary:
      'Track financial throughput, spend posture, and compliance signals across operational views.',
    intro:
      'This process is suited for KPI-focused walkthroughs. Start with executive dashboards, then drill into reports and claims posture. Explain where each click deepens insight and how to turn a metric into an action.',
    steps: [
      {
        action: 'Open Finance Dashboard',
        clickPath: 'Sidebar > Reports > Finance Dashboard',
        destination: '/finance-dashboard',
        detail: 'Provides a high-level analytics overview for current financial operations.',
      },
      {
        action: 'Open Spend Intelligence',
        clickPath: 'Sidebar > Reports > Spend Intelligence',
        destination: '/spend-analytics',
        detail: 'Highlights spend trends and categories for optimization analysis.',
      },
      {
        action: 'Open reports workbench',
        clickPath: 'Sidebar > Reports > Reports',
        destination: '/reports',
        detail: 'Consolidates report-level exports and view-driven analysis patterns.',
      },
      {
        action: 'Open VAT claims tracking',
        clickPath: 'Sidebar > Reports > VAT Claims',
        destination: '/vat-claims',
        detail: 'Supports tax-oriented validation and claim lifecycle tracking.',
      },
    ],
  },
  {
    id: 'automation-and-controls',
    title: 'Automation, Scheduling, And Control Plane Settings',
    icon: Clock,
    route: '/scheduler',
    summary:
      'Configure platform-level automation and governance controls that shape recurring operations.',
    intro:
      'This is a strong process for advanced admin training. Emphasize how recurring jobs, integration settings, and SLA configurations reduce manual overhead and improve consistency over time.',
    steps: [
      {
        action: 'Open scheduler',
        clickPath: 'Sidebar > Administration > Scheduler & Automation',
        destination: '/scheduler',
        detail: 'Manages recurring automation jobs and operational schedules.',
      },
      {
        action: 'Review approval SLA rules',
        clickPath: 'Sidebar > Administration > Approval SLA Management',
        destination: '/approval-slas',
        detail: 'Defines timing expectations and escalation behavior for approval workflows.',
      },
      {
        action: 'Review finance integration controls',
        clickPath: 'Sidebar > Settings > Finance Integrations',
        destination: '/finance-integrations',
        detail: 'Validates integration endpoints and posting behavior boundaries.',
      },
      {
        action: 'Review API token governance',
        clickPath: 'Sidebar > Administration > API Tokens',
        destination: '/api-tokens',
        detail: 'Controls technical access and service-to-service authentication surfaces.',
      },
    ],
  },
];

const miniProcesses = [
  { name: 'Platform pricing governance', path: '/platform/pricing', purpose: 'Review commercial configuration and pricing posture.' },
  { name: 'License and usage posture', path: '/license-status', purpose: 'Monitor capacity and subscription alignment.' },
  { name: 'OCR and storage operational health', path: '/platform/resources', purpose: 'Validate shared infrastructure readiness.' },
  { name: 'Company activity feed monitoring', path: '/activity-feed', purpose: 'Observe near-real-time operational actions.' },
  { name: 'Budget oversight', path: '/budget-analytics', purpose: 'Track budget adherence and trends.' },
  { name: 'Project controls', path: '/projects', purpose: 'Maintain project dimensions used in financial allocation.' },
  { name: 'Currency and rates administration', path: '/currencies', purpose: 'Maintain currency definitions and behavior.' },
  { name: 'Exchange rates management', path: '/exchange-rates', purpose: 'Maintain conversion rates before reconciliation.' },
  { name: 'Financial year setup', path: '/financial-years', purpose: 'Control accounting period boundaries.' },
  { name: 'Accrual review', path: '/accruals', purpose: 'Handle period-end financial adjustments.' },
  { name: 'All documents overview', path: '/all-documents', purpose: 'Browse centralized document inventory and status.' },
  { name: 'Notification preferences', path: '/notification-preferences', purpose: 'Tune user-level alerts and reminders.' },
];

// ─── Voice Scripts — Suno / Podcast / Training Narration (one per process) ─────────────
const voiceScripts = {
  'vendor-management': `Welcome to SpocoDoc Platform Training. In this session we are covering Vendor Management — the process that controls how suppliers enter the system, get reviewed, and become ready to support invoice and payment workflows. Follow along on screen as I click through every step.

Start at the left sidebar and navigate to Action Items, then select Vendors. This opens your master supplier registry. Every vendor connected to this company appears here along with their name, category, country, and operational status. The Status column is the most important field to check. Only Active or Approved vendors can receive invoices that flow through the approval chain. Any vendor showing Pending or Inactive is blocked from processing until the record is completed and validated.

Click into any vendor row to open the full detail view. Here you will see registration information, payment terms, default currency, and contact details. The payment information and GL category assignment are critical fields — they directly affect how this supplier’s invoices get coded and routed when they arrive for approval. Incomplete records here cause exceptions downstream.

Now navigate to Finance in the sidebar, then Subscriptions. For vendors providing recurring services, verify that their subscription record here aligns with what you expect to see on their invoices. A mismatch between subscription terms and invoice amounts is one of the most frequent causes of approval exceptions in high-volume accounts payable environments. Catching this at vendor setup saves significant time later.

Finally, navigate to Action Items and open Invoice Approvals. Observe how clean vendor governance translates directly into a well-functioning approval queue. Invoices from complete, accurate vendor records route smoothly and require minimal manual intervention. Incomplete vendor records generate exceptions, holds, and escalations that slow down the entire finance operation.

The key takeaway: treat vendor setup as a governance checkpoint, not just a data entry task. Verify every field, check subscription alignment for recurring suppliers, and resolve any Pending statuses before the first invoice arrives.`,

  'invoice-management': `Welcome back to SpocoDoc training. In this session we are walking through Invoice Management from start to finish — the workflow most users interact with every day. Understanding it in depth makes a real difference to how smoothly your accounts payable runs. Let us walk through each step on screen together.

Open the sidebar and navigate to Finance, then Invoices. This is your operational invoice inbox. Every invoice in the system, regardless of how it entered, appears here. The Status column tracks where each invoice is in its journey: Draft, Pending Review, Pending Approval, Approved, or Posted. Use the filter and sort controls at the top to focus on what needs your attention first. On screen I am filtering by Pending Review to find invoices that need checking before they advance to the approval queue.

Now let us look at invoice intake. Navigate to the upload area within the Invoices module. SpocoDoc supports OCR document extraction, which reads key fields automatically from uploaded invoices — supplier name, invoice number, issue date, due date, total amount, and line items. After upload, always review the extracted data carefully before saving. OCR accuracy depends on document quality, so spot-check the supplier name, invoice totals, and due date against the original document before proceeding.

Open any invoice to review its full detail. The detail view shows header information, all line items with amounts, supplier details, attached documents, and the coding section. Coding is where each invoice line gets assigned to the correct GL account, department, project, and budget line. This is the most finance-critical step in the process. Incorrect coding creates reconciliation problems and budget inaccuracies that are difficult to fix after posting. Ensure every line item has a valid GL account before submitting for approval.

Once the invoice is coded and reviewed, it enters the approval queue. Navigate to Action Items, then Invoice Approvals. Approvers review the complete record, verify coding, and make their approve or reject decision. After approval, navigate to Post to Finance to complete the final posting step, which moves the record into the accounting integration.

The three disciplines that keep invoice management healthy are: validate OCR data after upload, confirm coding before submission, and monitor the approval queue daily to prevent backlog. A clean invoice pipeline means accurate financial reporting and satisfied suppliers.`,

  'expense-management': `Hello and welcome to SpocoDoc training. Today we are covering Expense Management — how employees submit expense claims, how managers and finance teams approve them, and how the data feeds into financial reporting. This is a high-frequency workflow, so let us walk through it step by step.

Start at the sidebar under My Expenses and click My Expenses. This view shows all personal claims submitted by the current user along with their status and any approval notes. As an employee this is your primary workspace for tracking submissions, following up on pending claims, and correcting rejected items. Pay attention to rejection notes — they tell you exactly what to fix before resubmitting.

Now navigate to Submit Expense. The expense form captures date, category, amount, currency, and description. Attach receipts at this stage if your company policy requires them — claims without supporting documentation are routinely rejected during review. Select the correct department and project codes, then submit. The claim enters the approval queue immediately.

Now let us switch to the approver perspective. Navigate to Action Items, then Expense Approvals. This queue shows all pending claims assigned to you for review. Click any claim to see the full submission: the employee’s details, claim amount, category, attached receipts, and coding. Your review focuses on three things: is the amount reasonable for the category, is a receipt present and legible, and does the description make clear business sense? Approve or reject with a written note. Rejected claims return to the employee so they can correct and resubmit.

Finance teams should monitor expense analytics available under Reports — VAT Claims and Finance Dashboard. These views aggregate approved claims by department, category, and period, supporting period-end reporting and VAT reclaim processes. The expense process works well when employees submit promptly with complete information and approvers review within the SLA window.`,

  'budget-management': `Welcome to one of the most important modules in the SpocoDoc training series — Budget Management. This process governs how your organisation plans financial resources for the year, tracks actual spending against those plans in real time, and maintains control throughout the financial cycle. There are several stages involved, so let me walk through each one carefully on screen.

The annual budget cycle begins with Finance Admin creating a budget header. Navigate to the sidebar, open Budgets and Projects, and select Budgets. Click Create Budget Header. Enter the Budget Name, Budget Type, Financial Year, and Total Budget amount. Set the Status to Draft — this is your planning container, not yet active. On screen I am creating a header for the Operations department for the upcoming financial year.

With the header in Draft, Department Managers add the detailed budget lines. Open the budget header and select View Lines, then Add Line. Each line captures a specific planned spend item with the following fields: GL Account, Description, Budgeted Amount, Department, Project, Cost Centre, and Line Type — either one-off, new service, or continuity. If the cost is spread over several months, set the Spread Months and Spread Start Date fields. For large departments with many items, use the Import function to upload lines from a spreadsheet template. Make sure every GL Account is valid and every amount is realistic before proceeding.

When all lines are in place, Finance Admin validates and activates the budget. Open the header, use the Status action to submit and then activate. This moves the budget from Draft to Active. Active budgets are now the live control layer — every invoice approval that codes to this department checks available budget before proceeding. Watch as the status changes and the budget becomes visible in the approval coding interface.

During the financial year, budgets are consumed through invoice approvals and expense coding. When approvers code invoice lines, they assign each line to a GL account and budget line. This allocation immediately reduces the remaining available balance. The system tracks four amounts in real time: Budgeted, Spent, Committed, and Remaining. Navigate to Budget Analytics for trend data, Monthly View for period breakdown, and Manage Budget for a live snapshot of every department.

At year end, review the analytics and identify items not fully utilised or needing carry-forward. Open next year’s budget header, import prior year lines as a starting template, and adjust each line to reflect actual expected costs for the coming year. When the prior year is complete, Finance Admin closes it using the Close action. Closed budgets remain visible in historical reports and audit trails but are locked from further modification.

Budget management is the discipline that keeps financial decisions connected to reality. The critical habits are: keep budget lines current throughout the year, review variance reports regularly, code invoices accurately during approval, and plan each new year with real data rather than copying prior year assumptions without adjustment.`,

  'department-management': `Welcome back to SpocoDoc training. This session covers Department Management — how you structure your organisation within the platform, assign users to departments, and configure the boundaries that govern approvals and reporting. This is a foundational setup process that you will revisit whenever the organisation changes.

Navigate to Administration in the sidebar and select Departments. This view shows your organisational structure within SpocoDoc. Each department record represents an operational unit with a name, a manager, and optionally a parent department for hierarchical organisations. The departments defined here directly control how budget lines, invoice coding, and approval routing are organised. If your company has a Finance team and an IT team, each needs its own record with the correct manager assigned.

Next, configure user-department assignments. Navigate to Administration, then User Departments. Here you link individual user accounts to one or more departments. Department membership controls what data each user can see and what approvals they can perform. A user assigned as manager of the Operations department will see Operations invoices in their approval queue and can manage Operations budget lines. On screen I am assigning a newly created user to the Finance department with a Manager role.

Now review the role permissions. Go to Administration, then Role Permissions. This view shows every role and its specific capabilities. As a Platform Admin you can tune individual permissions. Apply the principle of least privilege: users should have exactly what they need to do their job, and nothing beyond that. Verify your setup by checking the live approval queues and confirming that documents are routing to the correct approver based on department coding.`,

  'company-onboarding': `Hello and welcome to SpocoDoc Platform Training. Today we are covering Company Onboarding — the process a Platform Admin follows every time a new customer company joins the platform. Whether you are onboarding your first company or your fiftieth, this workflow ensures the company is fully configured before live users begin transacting.

Start at the sidebar under Platform and select Companies. This is the master company registry for the entire platform. Click the company record for the one you need to configure, or create a new record if this is a brand new company. The company profile is your central control point — it shows active status, assigned modules, subscription tier, and configuration state.

Verify that the company name, registration number, default currency, and time zone are all correct at this stage. These base settings affect financial year boundaries, exchange rate handling, and date display across all modules. Getting these right at the start prevents data inconsistencies that are very difficult to fix after transactions begin.

Next, open company-specific configuration. In the sidebar, now in the context of this company, navigate to Administration then Company Settings. Work through each section systematically: approval routing rules, budget control settings, OCR processing preferences, and notification templates. On screen I am configuring the escalation path for invoices above a certain amount threshold.

Verify access and module readiness before any user logs in. Check Platform Settings to confirm integration connections are active. Visit OCR Pool and Storage to verify document processing capacity is provisioned. Open Subscriptions and Usage to confirm the subscription tier supports expected transaction volume. Then perform a live smoke test: create a test user, assign them to a department, submit a test invoice, and verify it routes to the correct approver. A well-onboarded company means end users have a platform that works correctly from their very first login.`,

  'identity-and-access': `Welcome to SpocoDoc Platform Training. This session covers Identity, Roles, and Access Governance — the security foundation that determines who can access what in the system. The right access structure protects data integrity, supports compliance requirements, and prevents unauthorised actions. Let us walk through each step on screen.

Start at Platform in the sidebar and select Platform Users. This is the central identity registry for all users across the platform. Here you can see every account, their assigned role, company context, and last activity timestamp. When creating a new user, enter their details, assign their primary role, and set their initial company context. The role selection here is consequential — it directly controls which menu items, features, and data this person will see when they log in.

With the user account created, assign them to the relevant department. Navigate to Administration, then User Departments. Find the new user and link them to the appropriate department. A user can belong to multiple departments if they have cross-departmental responsibilities, but each membership should reflect a genuine operational accountability. This assignment governs the user’s approval queue routing and visibility into budget and financial data.

Review the permission configuration for each role. Go to Administration, then Role Permissions. This lets you tune individual permissions across roles. Regularly review these settings and enforce least-privilege access — users should have the minimum permissions required to perform their specific job function. If someone changes roles, update their permissions and department assignments before they access the system in their new capacity.

Close the governance loop with the audit trail. Navigate to Monitoring, then Audit Logs. This records every significant action taken by every user — logins, data changes, approval decisions, and configuration edits. Use this view for compliance reviews, security investigations, and identifying unusual access patterns. When an issue arises, the audit log is where you find the evidence.`,

  'approval-operations': `Welcome back to SpocoDoc training. In this session we are covering Approval Operations — the daily workflow that drives your accounts payable, expense management, and finance posting processes. This is where financial commitments get reviewed, decided, and advanced toward the accounting system. If you work in finance or as an approver, this is your primary daily workspace.

Start with invoice approvals. Navigate to Action Items, then Invoice Approvals. This queue shows all invoices pending your review. You will see the invoice number, supplier, amount, submission date, and current status. Sort by due date to prioritise urgent items and filter by status to separate new arrivals from already-reviewed items. Click into an invoice to open the full detail view. Before approving, verify three things: does this invoice correspond to an expected purchase, is the coding correct, and is the amount within policy thresholds? If everything checks out, click Approve. If not, click Reject and write a clear note explaining what needs to be corrected.

For expense approvals, navigate to Action Items, then Expense Approvals. The process is similar but the source is employee expense claims. Open any pending claim and review the amount, category, receipt attachment, and description. Approvers are the last control point before company funds are committed, so apply consistent judgment: is the claim reasonable, is it supported by documentation, and does it comply with company policy?

Some invoices may require rate reconciliation before posting. Check Action Items, Rate Reconciliation for any flagged items. These are cases where the exchange rate used at coding time differs from the rate at posting time. Resolve these items to ensure financial records are accurate before they reach the ledger.

When invoices have been approved and are ready for the accounting system, navigate to Action Items, then Post to Finance. Review the posting batch, confirm the items look correct, and submit. This completes the invoice lifecycle from intake to ledger. The approval process runs on discipline and speed — clear policies, daily queue reviews, and decisive decision-making keep the accounts payable pipeline healthy.`,

  'support-incident': `Hello and welcome to SpocoDoc Platform Admin Training. In this session we are looking at Support Ticket and Incident Handling — how platform administrators investigate and resolve issues raised by users or identified through monitoring. Good incident handling is systematic, evidence-based, and clearly documented.

Start at Monitoring in the sidebar and select Support Tickets. This is the active ticket queue. Each ticket shows the submitting user, company context, severity level, status, and creation time. Sort by severity to ensure critical issues receive priority attention. Open a ticket and read the full description of what the user experienced. Before investigating, note the timestamp and the specific action the user was trying to perform — this is your starting point.

To trace what happened, navigate to Administration, then User Activity. This view shows a chronological log of actions taken by the affected user. Find the entries near the time the issue occurred. Often you can see exactly what the user was doing when the problem appeared. This evidence helps you distinguish between a system configuration issue, a permission problem, and user error.

For deeper investigation, open Monitoring, then Audit Logs. Filter by the affected user and the relevant time window. The audit log captures configuration changes, role updates, and permission modifications. If a user suddenly lost access to a feature they previously had, the audit log will show when and by whom that change was made. If the symptoms suggest a capacity issue, check OCR Pool and Storage and Subscriptions and Usage.

Once you identify the root cause, resolve the ticket with a clear explanation: what was found, what was changed, and how to prevent recurrence. Document every step. A well-resolved ticket leaves a trail that supports future compliance reviews and helps identify recurring patterns.`,

  'finance-observability': `Welcome to this SpocoDoc training session on Finance Visibility and Performance Monitoring. This is the analytical layer of the platform where financial data becomes actionable insight. If you are a Finance Admin, Controller, or Senior Platform Administrator, these views support period-end reporting, budget oversight, and operational decision-making.

Start at the sidebar under Reports and select Finance Dashboard. This is your high-level financial overview for the current period. The dashboard shows total invoice volume, approval queue depth, spend by category, budget utilisation rates, and outstanding exceptions. Think of this as your daily headline view — it tells you quickly whether the financial operation is running smoothly or whether something needs attention. Scan the metric tiles first, then drill into any areas showing unexpected values.

For deeper spend analysis, navigate to Reports, then Spend Intelligence. This trend-focused view lets you analyse spending patterns over time broken down by vendor, department, GL account, or project. This is particularly useful for budget conversations and management reporting — when someone claims a department is underspent or overspent, this view gives you the data to verify. The visualisations here can be used directly in management presentations or board reporting packs.

Navigate to Reports, then Reports for the centralised export hub. Here you can generate structured reports across any combination of dimensions — period, department, company, approval status. Configure your standard monthly report here and save it for repeated use. Finally, visit Reports, then VAT Claims. For organisations that reclaim VAT, this view tracks the full lifecycle of reclaimable amounts from invoice submission through approval to claim submission. Review this at the start of each VAT period to ensure no eligible claims are missed before the submission window closes.

Finance visibility works best as a routine practice, not just a period-end activity. Teams that check these dashboards weekly resolve variances early and reach month-end with clean data rather than surprises.`,

  'automation-and-controls': `Welcome to the final module in the SpocoDoc Platform Admin Training series. In this session we are covering Automation, Scheduling, and Control Plane Settings — the configuration layer that governs how the platform runs automatically, how approval timelines are enforced, and how integrations stay healthy. Mastering this section means the platform operates consistently without constant manual oversight.

Start at Administration in the sidebar and select Scheduler and Automation. This view lists all scheduled jobs running on the platform — recurring automations for exchange rate updates, subscription renewal checks, document processing queues, and notification triggers. Each job shows its schedule, last run timestamp, and current status. On screen I am reviewing the exchange rate update job, which runs automatically at midnight to pull the latest rates from the configured provider. If any job shows a failed status, investigate and resolve it promptly — automation failures can have downstream effects on financial data accuracy.

Now navigate to Administration, then Approval SLA Management. These are the time-based rules that enforce how long an invoice or expense can sit unapproved before escalation. You can see a rule configured for invoice approvals — if an invoice remains unactioned for two business days, it automatically escalates to the approver’s manager. SLA rules are critical for maintaining accounts payable velocity and meeting supplier payment terms. Review these settings whenever approval queue backlogs develop.

Go to Settings, then Finance Integrations. This view shows all active connections between SpocoDoc and external accounting or ERP systems. Verify that each integration is showing a healthy connected status. An integration showing errors means invoices may not be posting correctly to the ledger. Check the endpoint configuration, authentication credentials, and posting frequency settings. Prioritise resolution when an integration fails — every hour it remains broken increases the reconciliation burden.

Finally, navigate to Administration, then API Tokens. This is the governance layer for technical access. Every external system or custom integration that communicates with SpocoDoc authenticates using a token managed here. Review active tokens regularly, revoke tokens no longer in use, and ensure each has a descriptive name so you can identify its purpose during a security review. Well-configured automation means the platform handles routine operations without manual intervention. Your role as Platform Admin is to monitor these settings regularly and maintain the integration layer so financial data flows accurately across all connected systems.`,
};

// ─── React Flow node types (module-level to avoid re-creation on render) ─────

const _rfH = { background: 'transparent', border: 'none', width: 6, height: 6 };
const _rfLaneColors = {
  platform: { fill: '#E0E7FF', stroke: '#6366F1', text: '#312E81' },
  finance:  { fill: '#DBEAFE', stroke: '#2563EB', text: '#1E3A5F' },
  dept:     { fill: '#D1FAE5', stroke: '#059669', text: '#065F46' },
  employee: { fill: '#FFEDD5', stroke: '#EA580C', text: '#7C2D12' },
  approver: { fill: '#F3E8FF', stroke: '#9333EA', text: '#581C87' },
};

function _RfTask({ data, selected }) {
  const c = data.customColors || _rfLaneColors[data.lane] || { fill: '#F8FAFC', stroke: '#64748B', text: '#1E293B' };
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(data.label);
  const inputRef = React.useRef(null);

  const startEdit = (e) => { e.stopPropagation(); setDraft(data.label); setEditing(true); setTimeout(() => inputRef.current?.select(), 0); };
  const commitEdit = () => { setEditing(false); if (draft.trim() && data.onLabelChange) data.onLabelChange(draft.trim()); };
  const cancelEdit = () => { setEditing(false); setDraft(data.label); };

  return (
    <>
      <NodeResizer
        isVisible={selected}
        minWidth={80} minHeight={32}
        lineStyle={{ stroke: c.stroke, strokeWidth: 1.5 }}
        handleStyle={{ width: 9, height: 9, background: c.stroke, border: 'none', borderRadius: 2 }}
      />
      <div
        onDoubleClick={startEdit}
        title="Double-click to edit · Drag corner to resize"
        style={{ background: c.fill, border: `2px solid ${c.stroke}`, borderRadius: 6, width: '100%', height: '100%', minWidth: 80, minHeight: 32, padding: '4px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 11, fontWeight: 600, color: c.text, lineHeight: 1.3, boxSizing: 'border-box', cursor: 'default', overflow: 'hidden' }}
      >
        <Handle type="target" position={Position.Left}   id="tL" style={_rfH} />
        <Handle type="target" position={Position.Top}    id="tT" style={_rfH} />
        <Handle type="target" position={Position.Right}  id="tR" style={_rfH} />
        <Handle type="target" position={Position.Bottom} id="tB" style={_rfH} />
        <Handle type="source" position={Position.Left}   id="sL" style={_rfH} />
        <Handle type="source" position={Position.Top}    id="sT" style={_rfH} />
        <Handle type="source" position={Position.Right}  id="sR" style={_rfH} />
        <Handle type="source" position={Position.Bottom} id="sB" style={_rfH} />
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitEdit(); } if (e.key === 'Escape') cancelEdit(); }}
            style={{ width: '100%', fontSize: 11, fontWeight: 600, color: c.text, background: 'white', border: `1px solid ${c.stroke}`, borderRadius: 3, padding: '1px 4px', outline: 'none', textAlign: 'center', minWidth: 0 }}
          />
        ) : (
          <span style={{ pointerEvents: 'none' }}>{data.label}</span>
        )}
      </div>
    </>
  );
}

function _RfGateway({ data, selected }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(data.label);
  const inputRef = React.useRef(null);
  const startEdit = (e) => { e.stopPropagation(); setDraft(data.label); setEditing(true); setTimeout(() => inputRef.current?.select(), 0); };
  const commitEdit = () => { setEditing(false); if (draft.trim() && data.onLabelChange) data.onLabelChange(draft.trim()); };
  const cancelEdit = () => { setEditing(false); setDraft(data.label); };
  return (
    <>
      <NodeResizer isVisible={selected} minWidth={44} minHeight={44}
        lineStyle={{ stroke: '#F59E0B', strokeWidth: 1.5 }}
        handleStyle={{ width: 8, height: 8, background: '#F59E0B', border: 'none', borderRadius: 2 }}
      />
      <div onDoubleClick={startEdit} title="Double-click to edit label · Drag corner to resize" style={{ width: '100%', height: '100%', position: 'relative', minWidth: 44, minHeight: 44 }}>
        <Handle type="target" position={Position.Left}   id="tL" style={_rfH} />
        <Handle type="target" position={Position.Top}    id="tT" style={_rfH} />
        <Handle type="target" position={Position.Bottom} id="tB" style={_rfH} />
        <Handle type="source" position={Position.Right}  id="sR" style={_rfH} />
        <Handle type="source" position={Position.Top}    id="sT" style={_rfH} />
        <Handle type="source" position={Position.Left}   id="sL" style={_rfH} />
        <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
          <polygon points="50%,0 100%,50% 50%,100% 0,50%" fill="#FFFBEB" stroke="#F59E0B" strokeWidth="2" />
          <text x="50%" y="55%" textAnchor="middle" fill="#92400E" fontSize="14" fontWeight="700">✕</text>
        </svg>
        {editing ? (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
            <input
              ref={inputRef}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitEdit(); } if (e.key === 'Escape') cancelEdit(); }}
              style={{ fontSize: 9, fontWeight: 600, color: '#92400E', background: 'white', border: '1px solid #F59E0B', borderRadius: 3, padding: '1px 5px', outline: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}
            />
          </div>
        ) : (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: '50%', transform: 'translateX(-50%)', fontSize: 9, fontWeight: 600, color: '#92400E', whiteSpace: 'nowrap', pointerEvents: 'none' }}>{data.label}</div>
        )}
      </div>
    </>
  );
}

function _RfStartEnd({ data }) {
  const s = data.variant === 'start';
  return (
    <div style={{ width: 26, height: 26, position: 'relative' }}>
      <svg width={26} height={26}>
        <circle cx={13} cy={13} r={12} fill={s ? '#D1FAE5' : '#FEE2E2'} stroke={s ? '#22C55E' : '#EF4444'} strokeWidth={2} />
        {!s && <circle cx={13} cy={13} r={7} fill="#EF4444" />}
      </svg>
      {s
        ? <Handle type="source" position={Position.Right} id="sR" style={_rfH} />
        : <Handle type="target" position={Position.Left}  id="tL" style={_rfH} />
      }
      <div style={{ position: 'absolute', top: 28, left: '50%', transform: 'translateX(-50%)', fontSize: 9, fontWeight: 600, color: s ? '#065F46' : '#991B1B', whiteSpace: 'nowrap' }}>
        {s ? 'START' : 'END'}
      </div>
    </div>
  );
}

function _RfLane() {
  return <div style={{ pointerEvents: 'none' }} />;
}

const _rfNodeTypes = { task: _RfTask, gateway: _RfGateway, startEnd: _RfStartEnd, lane: _RfLane };

// Node color palette (fill / stroke / text)
const _PALETTE = [
  { fill: '#DBEAFE', stroke: '#2563EB', text: '#1E3A5F', label: 'Blue' },
  { fill: '#D1FAE5', stroke: '#059669', text: '#065F46', label: 'Green' },
  { fill: '#FEF3C7', stroke: '#D97706', text: '#78350F', label: 'Amber' },
  { fill: '#FCE7F3', stroke: '#DB2777', text: '#831843', label: 'Pink' },
  { fill: '#E0E7FF', stroke: '#6366F1', text: '#312E81', label: 'Indigo' },
  { fill: '#FFEDD5', stroke: '#EA580C', text: '#7C2D12', label: 'Orange' },
  { fill: '#F3E8FF', stroke: '#9333EA', text: '#581C87', label: 'Purple' },
  { fill: '#FEE2E2', stroke: '#DC2626', text: '#7F1D1D', label: 'Red' },
  { fill: '#F1F5F9', stroke: '#475569', text: '#1E293B', label: 'Grey' },
  { fill: '#ECFDF5', stroke: '#10B981', text: '#064E3B', label: 'Teal' },
];

const _RF_LANES = [
  { label: 'Platform Admin', fill: 'rgba(224,231,255,0.5)', accent: '#6366F1', text: '#312E81' },
  { label: 'Finance Admin',  fill: 'rgba(219,234,254,0.5)', accent: '#2563EB', text: '#1E3A5F' },
  { label: 'Dept. Manager',  fill: 'rgba(209,250,229,0.5)', accent: '#059669', text: '#065F46' },
  { label: 'Employee',       fill: 'rgba(255,237,213,0.5)', accent: '#EA580C', text: '#7C2D12' },
  { label: 'Approver',       fill: 'rgba(243,232,255,0.5)', accent: '#9333EA', text: '#581C87' },
];

const _LW = 1650;

const _rfInitNodes = [
  // Lane backgrounds
  ..._RF_LANES.map((l, i) => ({
    id: `lane-${i}`, type: 'lane',
    position: { x: -50, y: i * 130 },
    data: {},
    selectable: false, draggable: false, focusable: false,
    style: { width: _LW + 50, height: 130, background: l.fill, borderBottom: '1px solid #CBD5E1', pointerEvents: 'none', zIndex: -10 },
  })),
  // Start / End
  { id: 'start', type: 'startEnd', position: { x: 0,    y: 52 }, data: { variant: 'start' }, draggable: false, selectable: false },
  { id: 'end',   type: 'startEnd', position: { x: 1490, y: 52 }, data: { variant: 'end'   }, draggable: false, selectable: false },
  // Platform (y=45, lane mid=65)
  { id: 'company-setup',    type: 'task', position: { x: 71,   y: 45 }, style: { width: 152, height: 40 }, data: { lane: 'platform', w: 152, label: 'Create Company & Settings' } },
  { id: 'users-roles',      type: 'task', position: { x: 244,  y: 45 }, style: { width: 155, height: 40 }, data: { lane: 'platform', w: 155, label: 'Provision Users & Roles' } },
  { id: 'fin-integrations', type: 'task', position: { x: 422,  y: 45 }, style: { width: 168, height: 40 }, data: { lane: 'platform', w: 168, label: 'Finance Integrations & Scheduler' } },
  { id: 'platform-monitor', type: 'task', position: { x: 1314, y: 45 }, style: { width: 155, height: 40 }, data: { lane: 'platform', w: 155, label: 'Audit Logs & Platform Monitor' } },
  // Finance (y=175, lane mid=195)
  { id: 'fin-year',         type: 'task',    position: { x: 158, y: 175 }, style: { width: 148, height: 40 }, data: { lane: 'finance', w: 148, label: 'Configure Financial Year' } },
  { id: 'budget-draft',     type: 'task',    position: { x: 323, y: 175 }, style: { width: 158, height: 40 }, data: { lane: 'finance', w: 158, label: 'Create Budget Header (Draft)' } },
  { id: 'gateway',          type: 'gateway', position: { x: 512, y: 173 }, style: { width: 44,  height: 44 }, data: { label: 'Lines OK?' } },
  { id: 'budget-activate',  type: 'task',    position: { x: 603, y: 175 }, style: { width: 158, height: 40 }, data: { lane: 'finance', w: 158, label: 'Validate & Activate Budget' } },
  { id: 'invoice-review',   type: 'task',    position: { x: 807, y: 175 }, style: { width: 150, height: 40 }, data: { lane: 'finance', w: 150, label: 'Upload & Review Invoices' } },
  { id: 'post-finance',     type: 'task',    position: { x: 1127, y: 175 }, style: { width: 150, height: 40 }, data: { lane: 'finance', w: 150, label: 'Post Approved Invoices' } },
  { id: 'budget-analytics', type: 'task',    position: { x: 1293, y: 175 }, style: { width: 158, height: 40 }, data: { lane: 'finance', w: 158, label: 'Budget Analytics & Reporting' } },
  // Dept (y=305, lane mid=325)
  { id: 'dept-structure',   type: 'task', position: { x: 248,  y: 305 }, style: { width: 148, height: 40 }, data: { lane: 'dept', w: 148, label: 'Define Dept. Structure' } },
  { id: 'budget-lines',     type: 'task', position: { x: 446,  y: 305 }, style: { width: 172, height: 40 }, data: { lane: 'dept', w: 172, label: 'Add Budget Lines (GL/Project/CC)' } },
  { id: 'code-inv-lines',   type: 'task', position: { x: 969,  y: 305 }, style: { width: 165, height: 40 }, data: { lane: 'dept', w: 165, label: 'Code Invoice Lines to GL & Budget' } },
  { id: 'budget-variance',  type: 'task', position: { x: 1193, y: 305 }, style: { width: 158, height: 40 }, data: { lane: 'dept', w: 158, label: 'Monitor Dept. Budget Variance' } },
  // Employee (y=435, lane mid=455)
  { id: 'submit-expense',   type: 'task', position: { x: 808,  y: 435 }, style: { width: 148, height: 40 }, data: { lane: 'employee', w: 148, label: 'Submit Expense Claim' } },
  // Approver (y=565, lane mid=585)
  { id: 'exp-approval',     type: 'task', position: { x: 808,  y: 565 }, style: { width: 148, height: 40 }, data: { lane: 'approver', w: 148, label: 'Approve / Reject Expense' } },
  { id: 'inv-approval',     type: 'task', position: { x: 978,  y: 565 }, style: { width: 148, height: 40 }, data: { lane: 'approver', w: 148, label: 'Approve / Reject Invoice' } },
];

const _rfES = { stroke: '#6B7280', strokeWidth: 1.5 };
const _rfED = { stroke: '#6B7280', strokeWidth: 1.5, strokeDasharray: '5 4' };
const _rfM  = { type: 'arrowclosed', color: '#6B7280', width: 11, height: 8 };
const _rfLbl = (txt, ok) => ({ label: txt, labelStyle: { fontSize: 9, fontWeight: 700 }, labelBgStyle: { fill: ok ? '#D1FAE5' : '#FEE2E2', padding: 2 }, labelBgBorderRadius: 3 });

const _rfInitEdges = [
  { id: 'e1',  source: 'start',           target: 'company-setup',    type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e2',  source: 'company-setup',   target: 'users-roles',      type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e3',  source: 'users-roles',     target: 'fin-integrations', type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e4',  source: 'company-setup',   target: 'fin-year',         type: 'smoothstep', sourceHandle: 'sB', targetHandle: 'tT', style: _rfED, markerEnd: _rfM },
  { id: 'e5',  source: 'users-roles',     target: 'dept-structure',   type: 'smoothstep', sourceHandle: 'sB', targetHandle: 'tT', style: _rfED, markerEnd: _rfM },
  { id: 'e6',  source: 'fin-year',        target: 'budget-draft',     type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e7',  source: 'budget-draft',    target: 'gateway',          type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e8',  source: 'dept-structure',  target: 'budget-lines',     type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e9',  source: 'budget-lines',    target: 'gateway',          type: 'smoothstep', sourceHandle: 'sT', targetHandle: 'tB', style: _rfED, markerEnd: _rfM },
  { id: 'e10', source: 'gateway',         target: 'budget-activate',  type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM, ..._rfLbl('OK', true) },
  { id: 'e11', source: 'budget-activate', target: 'invoice-review',   type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e12', source: 'invoice-review',  target: 'inv-approval',     type: 'smoothstep', sourceHandle: 'sB', targetHandle: 'tT', style: _rfES, markerEnd: _rfM },
  { id: 'e13', source: 'submit-expense',  target: 'exp-approval',     type: 'smoothstep', sourceHandle: 'sB', targetHandle: 'tT', style: _rfES, markerEnd: _rfM },
  { id: 'e14', source: 'exp-approval',    target: 'post-finance',     type: 'smoothstep', sourceHandle: 'sL', targetHandle: 'tB', style: _rfES, markerEnd: _rfM, ..._rfLbl('Approved', true) },
  { id: 'e15', source: 'exp-approval',    target: 'submit-expense',   type: 'smoothstep', sourceHandle: 'sL', targetHandle: 'tL', style: _rfED, markerEnd: _rfM, ..._rfLbl('Rejected', false) },
  { id: 'e16', source: 'inv-approval',    target: 'post-finance',     type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tB', style: _rfES, markerEnd: _rfM, ..._rfLbl('Approved', true) },
  { id: 'e17', source: 'inv-approval',    target: 'invoice-review',   type: 'smoothstep', sourceHandle: 'sL', targetHandle: 'tB', style: _rfED, markerEnd: _rfM, ..._rfLbl('Rejected', false) },
  { id: 'e18', source: 'post-finance',    target: 'code-inv-lines',   type: 'smoothstep', sourceHandle: 'sB', targetHandle: 'tT', style: _rfED, markerEnd: _rfM },
  { id: 'e19', source: 'code-inv-lines',  target: 'budget-variance',  type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e20', source: 'post-finance',    target: 'budget-analytics', type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
  { id: 'e21', source: 'budget-variance', target: 'budget-analytics', type: 'smoothstep', sourceHandle: 'sT', targetHandle: 'tB', style: _rfED, markerEnd: _rfM },
  { id: 'e22', source: 'budget-analytics',target: 'platform-monitor', type: 'smoothstep', sourceHandle: 'sT', targetHandle: 'tB', style: _rfED, markerEnd: _rfM },
  { id: 'e23', source: 'platform-monitor',target: 'end',              type: 'smoothstep', sourceHandle: 'sR', targetHandle: 'tL', style: _rfES, markerEnd: _rfM },
];

// ─── SVG Swimlane Utilities ──────────────────────────────────────────────────

function wrapText(text, maxChars) {
  if (!text) return [''];
  const words = text.split(' ');
  const lines = [];
  let cur = '';
  for (const w of words) {
    const candidate = cur ? `${cur} ${w}` : w;
    if (candidate.length > maxChars && cur) { lines.push(cur); cur = w; }
    else cur = candidate;
  }
  if (cur) lines.push(cur);
  return lines.slice(0, 3);
}

// ─── Master End-to-End Swimlane (React Flow — drag nodes to rearrange) ─────────

// Lane label overlay — rendered inside ReactFlow so it follows zoom + pan
function _LaneLabelsPanel({ lanes }) {
  const { y, zoom } = useViewport();
  return (
    <div style={{ position: 'absolute', left: 0, top: 0, width: 108, height: '100%', pointerEvents: 'none', zIndex: 5, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, width: 108, height: '100%', background: 'white', borderRight: '1.5px solid #94A3B8' }} />
      {lanes.map((lane, i) => {
        const top = y + i * 130 * zoom;
        const height = 130 * zoom;
        return (
          <div key={i} style={{ position: 'absolute', top, left: 0, width: 108, height, background: lane.accent + '22', borderBottom: '1px solid #CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {height > 16 && (
              <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: Math.max(7, Math.min(11, 10.5 * zoom)), fontWeight: 700, color: lane.text, userSelect: 'none', whiteSpace: 'nowrap' }}>{lane.label}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const _LANE_KEYS = ['platform', 'finance', 'dept', 'employee', 'approver'];

function MasterSwimlane() {
  const [nodes, setNodes, onNodesChange] = useNodesState(_rfInitNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(_rfInitEdges);
  const [newLabel, setNewLabel] = React.useState('');
  const [newLane, setNewLane] = React.useState('finance');
  const [lanes, setLanes] = React.useState(_RF_LANES);
  const [newLaneName, setNewLaneName] = React.useState('');
  const [containerHeight, setContainerHeight] = React.useState(660);
  const [newNodeType, setNewNodeType] = React.useState('task');
  const [newNodeColor, setNewNodeColor] = React.useState(null);
  const [selectedNodeIds, setSelectedNodeIds] = React.useState([]);

  const onSelectionChange = React.useCallback(({ nodes: sel }) => {
    setSelectedNodeIds(sel.filter(n => n.type !== 'lane').map(n => n.id));
  }, []);

  const applyColor = React.useCallback((colors) => {
    setNodes(nds => nds.map(n => selectedNodeIds.includes(n.id) ? { ...n, data: { ...n.data, customColors: colors } } : n));
  }, [selectedNodeIds, setNodes]);

  const startResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = containerHeight;
    const onMove = (ev) => setContainerHeight(Math.max(260, startH + ev.clientY - startY));
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const onConnect = React.useCallback(
    (params) => setEdges(eds => addEdge({ ...params, type: 'smoothstep', style: _rfES, markerEnd: _rfM }, eds)),
    [setEdges]
  );

  const updateNodeLabel = React.useCallback((id, label) => {
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, label } } : n));
  }, [setNodes]);

  // Inject onLabelChange into all task + gateway nodes whenever updateNodeLabel changes
  React.useEffect(() => {
    setNodes(nds => nds.map(n =>
      (n.type === 'task' || n.type === 'gateway') ? { ...n, data: { ...n.data, onLabelChange: (lbl) => updateNodeLabel(n.id, lbl) } } : n
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNodeLabel]);

  const handleDeleteSelected = React.useCallback(() => {
    setNodes(nds => nds.filter(n => !n.selected || n.type === 'lane'));
    setEdges(eds => eds.filter(e => !e.selected));
  }, [setNodes, setEdges]);

  const handleAddNode = () => {
    const isGateway = newNodeType === 'gateway';
    const label = newLabel.trim() || (isGateway ? 'Decision?' : 'New Task');
    const laneIdx = _LANE_KEYS.indexOf(newLane);
    const id = `node-${Date.now()}`;
    setNodes(nds => [...nds, {
      id,
      type: newNodeType,
      position: { x: 200 + Math.random() * 400, y: laneIdx * 130 + (isGateway ? 43 : 45) },
      style: isGateway ? { width: 44, height: 44 } : { width: 150, height: 40 },
      data: { lane: newLane, w: 150, label, customColors: newNodeColor || null, onLabelChange: (lbl) => updateNodeLabel(id, lbl) },
    }]);
    setNewLabel('');
  };

  const handleAddLane = () => {
    const name = newLaneName.trim();
    if (!name) return;
    const laneColors = ['#E2E8F0', '#FEF9C3', '#FCE7F3', '#E0F2FE', '#F0FDF4', '#FFF7ED'];
    const laneAccents = ['#64748B', '#EAB308', '#EC4899', '#0EA5E9', '#22C55E', '#F97316'];
    const idx = lanes.length;
    const accent = laneAccents[idx % laneAccents.length];
    const fill = laneColors[idx % laneColors.length];
    // Add lane key
    const key = `custom-lane-${idx}`;
    _LANE_KEYS.push(key);
    const newLaneObj = { label: name, fill: `${fill}80`, accent, text: accent };
    setLanes(prev => [...prev, newLaneObj]);
    // Add lane background node
    setNodes(nds => [...nds, {
      id: `lane-${idx}`, type: 'lane',
      position: { x: -50, y: idx * 130 },
      data: {},
      selectable: false, draggable: false, focusable: false,
      style: { width: _LW + 50, height: 130, background: `${fill}80`, borderBottom: '1px solid #CBD5E1', pointerEvents: 'none', zIndex: -10 },
    }]);
    setNewLaneName('');
  };

  const totalLaneH = lanes.length * 130;

  return (
    <div className="rounded-xl border border-gray-300 shadow-sm overflow-hidden bg-white">
      {/* Header */}
      <div style={{ background: '#1E3A5F', color: 'white', padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.3px' }}>SpocoDoc — End-to-End Business Process Flow</span>
        <span style={{ fontSize: 11, opacity: 0.75 }}>Drag nodes · Connect handles · Double-click to edit · Delete key · Drag ▼ bar to resize</span>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '7px 12px', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', alignItems: 'center' }}>
        {/* Type */}
        <span style={{ fontSize: 11, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Type:</span>
        <button onClick={() => setNewNodeType('task')}
          style={{ fontSize: 11, fontWeight: 600, background: newNodeType === 'task' ? '#2563EB' : 'white', color: newNodeType === 'task' ? 'white' : '#374151', border: '1px solid #CBD5E1', borderRadius: 5, padding: '3px 9px', cursor: 'pointer' }}
        >□ Task</button>
        <button onClick={() => setNewNodeType('gateway')}
          style={{ fontSize: 11, fontWeight: 600, background: newNodeType === 'gateway' ? '#D97706' : 'white', color: newNodeType === 'gateway' ? 'white' : '#374151', border: '1px solid #CBD5E1', borderRadius: 5, padding: '3px 9px', cursor: 'pointer' }}
        >◇ Gateway</button>

        <div style={{ width: 1, height: 20, background: '#CBD5E1', margin: '0 2px' }} />

        {/* Label */}
        <input
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAddNode()}
          placeholder={newNodeType === 'gateway' ? 'Decision label…' : 'Node label…'}
          style={{ fontSize: 11, border: '1px solid #CBD5E1', borderRadius: 5, padding: '3px 8px', width: 150, outline: 'none' }}
        />
        {/* Lane */}
        <select value={newLane} onChange={e => setNewLane(e.target.value)}
          style={{ fontSize: 11, border: '1px solid #CBD5E1', borderRadius: 5, padding: '3px 6px', background: 'white', outline: 'none' }}
        >
          {_LANE_KEYS.map((k, i) => <option key={k} value={k}>{lanes[i]?.label || k}</option>)}
        </select>
        {/* New-node default color */}
        <span style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>Color:</span>
        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {_PALETTE.map(p => (
            <button key={p.label} title={p.label}
              onClick={() => setNewNodeColor(newNodeColor?.label === p.label ? null : p)}
              style={{ width: 18, height: 18, borderRadius: '50%', background: p.fill, border: `2px solid ${newNodeColor?.label === p.label ? p.stroke : '#CBD5E1'}`, cursor: 'pointer', outline: newNodeColor?.label === p.label ? `2px solid ${p.stroke}` : 'none', outlineOffset: 1 }}
            />
          ))}
          {newNodeColor && <button onClick={() => setNewNodeColor(null)} style={{ fontSize: 9, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>}
        </div>
        <button onClick={handleAddNode}
          style={{ fontSize: 11, fontWeight: 600, background: '#2563EB', color: 'white', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer' }}
        >+ Add Block</button>

        <div style={{ width: 1, height: 20, background: '#CBD5E1', margin: '0 2px' }} />

        {/* Add lane */}
        <span style={{ fontSize: 11, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>+ Lane:</span>
        <input value={newLaneName} onChange={e => setNewLaneName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAddLane()}
          placeholder="Lane name…"
          style={{ fontSize: 11, border: '1px solid #CBD5E1', borderRadius: 5, padding: '3px 8px', width: 120, outline: 'none' }}
        />
        <button onClick={handleAddLane}
          style={{ fontSize: 11, fontWeight: 600, background: '#059669', color: 'white', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer' }}
        >+ Add Lane</button>

        <div style={{ width: 1, height: 20, background: '#CBD5E1', margin: '0 2px' }} />
        <button onClick={handleDeleteSelected}
          style={{ fontSize: 11, fontWeight: 600, background: '#DC2626', color: 'white', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer' }}
        >🗑 Delete selected</button>
      </div>

      {/* Selected-node color panel — appears when any node is selected */}
      {selectedNodeIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 12px', background: '#FFF7ED', borderBottom: '1px solid #FED7AA', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#92400E', whiteSpace: 'nowrap' }}>Change color ({selectedNodeIds.length} selected):</span>
          {_PALETTE.map(p => (
            <button key={p.label} title={p.label}
              onClick={() => applyColor(p)}
              style={{ width: 20, height: 20, borderRadius: '50%', background: p.fill, border: `2px solid ${p.stroke}`, cursor: 'pointer' }}
            />
          ))}
          <button onClick={() => applyColor(null)}
            style={{ fontSize: 10, fontWeight: 600, border: '1px solid #D97706', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', background: 'white', color: '#92400E' }}
          >Reset to lane color</button>
        </div>
      )}

      {/* React Flow — full width; lane labels are synced overlay inside canvas */}
      <div style={{ height: containerHeight }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onSelectionChange={onSelectionChange}
          nodeTypes={_rfNodeTypes}
          fitView
          fitViewOptions={{ padding: 0.08 }}
          minZoom={0.25}
          maxZoom={3}
          deleteKeyCode={['Delete', 'Backspace']}
          proOptions={{ hideAttribution: true }}
        >
          <_LaneLabelsPanel lanes={lanes} />
          <Background color="#e5e7eb" gap={28} size={1} />
          <Controls showInteractive={false} style={{ left: 116 }} />
          <MiniMap nodeStrokeWidth={3} zoomable pannable style={{ height: 80 }} />
        </ReactFlow>
      </div>

      {/* Vertical resize handle */}
      <div
        onMouseDown={startResize}
        style={{ height: 10, background: '#E2E8F0', cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none', borderTop: '1px solid #CBD5E1' }}
        title="Drag to resize diagram height"
      >
        <div style={{ width: 40, height: 3, background: '#94A3B8', borderRadius: 2 }} />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-5 px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-600">
        <div className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded-full inline-block border-2 border-green-500" style={{ background: '#D1FAE5' }} />Start / End Event</div>
        <div className="flex items-center gap-1.5"><span className="w-5 h-3.5 rounded inline-block border-2 border-blue-500" style={{ background: '#DBEAFE' }} />Task / Activity (colour = role lane)</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3.5 h-3.5 border-2 border-amber-500" style={{ background: '#FFFBEB', transform: 'rotate(45deg)' }} />Gateway — decision point</div>
        <div className="flex items-center gap-1.5"><svg width="28" height="12"><line x1="2" y1="6" x2="26" y2="6" stroke="#6B7280" strokeWidth="1.5" /></svg>Sequence flow</div>
        <div className="flex items-center gap-1.5"><svg width="28" height="12"><line x1="2" y1="6" x2="26" y2="6" stroke="#6B7280" strokeWidth="1.5" strokeDasharray="4 3" /></svg>Cross-lane handoff / feedback loop</div>
      </div>
    </div>
  );
}

// ─── Per-Process Swimlane (compact, 2–3 lanes) ────────────────────────────────

function ProcessSwimlane({ process }) {
  // We infer actor from step content
  const inferActor = (step) => {
    const t = `${step.action} ${step.detail} ${step.clickPath}`.toLowerCase();
    if (t.includes('platform') || t.includes('scheduler') || t.includes('api token') || t.includes('company')) return 'Platform Admin';
    if (t.includes('finance admin') || t.includes('budget header') || t.includes('activat') || t.includes('post to finance') || t.includes('financial year') || t.includes('fin-year')) return 'Finance Admin';
    if (t.includes('department manager') || t.includes('dept') || t.includes('budget line') || t.includes('department')) return 'Dept. Manager';
    if (t.includes('employee') || t.includes('expense') || t.includes('submit')) return 'Employee';
    if (t.includes('approv') || t.includes('reject') || t.includes('decision')) return 'Approver';
    return 'User';
  };

  const laneColors = {
    'Platform Admin': { fill: '#E0E7FF', stroke: '#6366F1', text: '#312E81' },
    'Finance Admin':  { fill: '#DBEAFE', stroke: '#2563EB', text: '#1E3A5F' },
    'Dept. Manager':  { fill: '#D1FAE5', stroke: '#059669', text: '#065F46' },
    'Employee':       { fill: '#FFEDD5', stroke: '#EA580C', text: '#7C2D12' },
    'Approver':       { fill: '#F3E8FF', stroke: '#9333EA', text: '#581C87' },
    'User':           { fill: '#F8FAFC', stroke: '#64748B', text: '#1E293B' },
  };

  // Assign actors and find unique lanes
  const stepsWithActors = process.steps.map(s => ({ ...s, actor: inferActor(s) }));
  const uniqueActors = [...new Set(stepsWithActors.map(s => s.actor))];

  const labelW = 90;
  const nodeW = 158;
  const nodeH = 44;
  const laneH = 84;
  const colGap = 178;
  const startX = labelW + 30;

  // We'll place steps L-to-R in timeline order
  // Steps that share the same actor can be in the same lane, just positioned at their column index
  const canvasW = startX + process.steps.length * colGap + 80;
  const canvasH = uniqueActors.length * laneH + 36;

  const laneIdx = (actor) => uniqueActors.indexOf(actor);
  const laneY = (idx) => 36 + idx * laneH;
  const laneMid = (idx) => laneY(idx) + laneH / 2;
  const nodeX = (stepIdx) => startX + stepIdx * colGap;

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto">
      <svg viewBox={`0 0 ${canvasW} ${canvasH}`} className="w-full h-auto min-w-[520px]" preserveAspectRatio="xMinYMin meet">
        <defs>
          <marker id={`arr-${process.id}`} markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L8,3 L0,6 z" fill="#6B7280" />
          </marker>
        </defs>

        {/* Lane backgrounds */}
        {uniqueActors.map((actor, idx) => {
          const colors = laneColors[actor] || laneColors['User'];
          return (
            <g key={actor}>
              <rect x={0} y={laneY(idx)} width={labelW} height={laneH} fill={colors.stroke} opacity="0.15" />
              <rect x={labelW} y={laneY(idx)} width={canvasW - labelW} height={laneH} fill={colors.fill} opacity="0.45" />
              <rect x={0} y={laneY(idx)} width={canvasW} height={laneH} fill="none" stroke="#CBD5E1" strokeWidth="1" />
              <text
                x={labelW / 2}
                y={laneY(idx) + laneH / 2}
                textAnchor="middle"
                fill={colors.text}
                fontSize="9.5"
                fontWeight="700"
                transform={`rotate(-90, ${labelW / 2}, ${laneY(idx) + laneH / 2})`}
              >
                {actor}
              </text>
            </g>
          );
        })}
        <line x1={labelW} y1={36} x2={labelW} y2={canvasH} stroke="#94A3B8" strokeWidth="1.2" />

        {/* Start event */}
        <circle cx={labelW + 14} cy={laneMid(laneIdx(stepsWithActors[0].actor))} r={10} fill="#D1FAE5" stroke="#22C55E" strokeWidth="1.8" />

        {/* Step nodes + connecting arrows */}
        {stepsWithActors.map((step, idx) => {
          const cx = nodeX(idx);
          const cy = laneMid(laneIdx(step.actor));
          const colors = laneColors[step.actor] || laneColors['User'];
          const lines = wrapText(step.action, 20);
          const lineH = 12;
          const totalTextH = lines.length * lineH;
          const textStartY = cy - nodeH / 2 + (nodeH - totalTextH) / 2 + lineH - 1;

          // Arrow from previous step (or start event)
          let arrowEl = null;
          if (idx === 0) {
            arrowEl = (
              <line
                x1={labelW + 24} y1={cy}
                x2={cx - nodeW / 2 - 3} y2={cy}
                stroke="#6B7280" strokeWidth="1.4"
                markerEnd={`url(#arr-${process.id})`}
              />
            );
          } else {
            const prev = stepsWithActors[idx - 1];
            const prevCx = nodeX(idx - 1);
            const prevCy = laneMid(laneIdx(prev.actor));
            const prevRight = prevCx + nodeW / 2;
            const thisLeft = cx - nodeW / 2;
            const thisColors = laneColors[step.actor] || laneColors['User'];
            const isDiffLane = laneIdx(step.actor) !== laneIdx(prev.actor);

            if (!isDiffLane) {
              arrowEl = (
                <line
                  x1={prevRight} y1={prevCy}
                  x2={thisLeft - 3} y2={cy}
                  stroke="#6B7280" strokeWidth="1.4"
                  markerEnd={`url(#arr-${process.id})`}
                />
              );
            } else {
              // Cross-lane: go right from prev, then down/up to this lane, then right to node
              const midX = prevRight + (thisLeft - prevRight) / 2;
              arrowEl = (
                <polyline
                  points={`${prevRight},${prevCy} ${midX},${prevCy} ${midX},${cy} ${thisLeft - 3},${cy}`}
                  fill="none"
                  stroke="#6B7280"
                  strokeWidth="1.4"
                  strokeDasharray="4 3"
                  markerEnd={`url(#arr-${process.id})`}
                />
              );
            }
          }

          return (
            <g key={`${process.id}-lane-step-${idx}`}>
              {arrowEl}
              <rect x={cx - nodeW / 2} y={cy - nodeH / 2} width={nodeW} height={nodeH} rx="6" fill={colors.fill} stroke={colors.stroke} strokeWidth="1.6" />
              {lines.map((ln, li) => (
                <text key={li} x={cx} y={textStartY + li * lineH} textAnchor="middle" fill={colors.text} fontSize="10" fontWeight="600">{ln}</text>
              ))}
              <text x={cx} y={cy + nodeH / 2 + 11} textAnchor="middle" fill="#6B7280" fontSize="8.5">Step {idx + 1}</text>
            </g>
          );
        })}

        {/* End event */}
        {(() => {
          const last = stepsWithActors[stepsWithActors.length - 1];
          const cx = nodeX(stepsWithActors.length - 1);
          const cy = laneMid(laneIdx(last.actor));
          return (
            <g>
              <line x1={cx + nodeW / 2} y1={cy} x2={cx + nodeW / 2 + 22} y2={cy} stroke="#6B7280" strokeWidth="1.4" markerEnd={`url(#arr-${process.id})`} />
              <circle cx={cx + nodeW / 2 + 36} cy={cy} r={10} fill="#FEE2E2" stroke="#EF4444" strokeWidth="1.8" />
              <circle cx={cx + nodeW / 2 + 36} cy={cy} r={6} fill="#EF4444" />
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

function ProcessCard({ process }) {
  const Icon = process.icon;
  const [showScript, setShowScript] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const script = voiceScripts[process.id];

  const copyScript = () => {
    navigator.clipboard.writeText(script || '').then(() => { setCopied(true); setTimeout(() => setCopied(false), 2200); });
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-cyan-50">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-900 leading-tight">{process.title}</h2>
            <p className="mt-1 text-sm text-gray-700">{process.summary}</p>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-700">
              <MapPin className="w-3.5 h-3.5" />
              Primary route: {process.route}
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 space-y-4">
        <p className="text-sm leading-6 text-gray-700">{process.intro}</p>

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Role Swimlane Diagram</h3>
          <ProcessSwimlane process={process} />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Screen Recording Script (Clicks And Destinations)</h3>
          <div className="space-y-3">
            {process.steps.map((step, idx) => (
              <div key={`${process.id}-step-${idx}`} className="rounded-lg border border-gray-200 px-3 py-3 bg-gray-50">
                <p className="text-sm font-semibold text-gray-900">Step {idx + 1}: {step.action}</p>
                <p className="mt-1 text-sm text-gray-700">
                  Click path: <span className="font-medium">{step.clickPath}</span>
                </p>
                <p className="text-sm text-gray-700">
                  Destination location: <span className="font-medium">{step.destination}</span>
                </p>
                <p className="mt-1 text-sm text-gray-600">{step.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {script && (
          <div>
            <button
              onClick={() => setShowScript(s => !s)}
              className="flex items-center gap-2 text-sm font-semibold rounded-lg border px-4 py-2 transition-colors"
              style={{ background: showScript ? '#7C3AED' : '#F5F3FF', color: showScript ? 'white' : '#5B21B6', borderColor: '#7C3AED' }}
            >
              <span>🎙</span>
              {showScript ? 'Hide Voice Script' : 'Show Voice Script — Training Narration (Suno / Podcast)'}
            </button>
            {showScript && (
              <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50 p-4 relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-purple-800 uppercase tracking-wide">Training Voice Script — Screen Recording Narration</span>
                  <button
                    onClick={copyScript}
                    style={{ fontSize: 11, fontWeight: 600, background: copied ? '#059669' : '#7C3AED', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', transition: 'background 0.2s' }}
                  >{copied ? '✓ Copied!' : '📋 Copy full script'}</button>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.75, color: '#374151', whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif' }}>{script}</div>
                <div className="mt-3 rounded-lg bg-purple-100 border border-purple-200 px-3 py-2">
                  <p className="text-xs text-purple-700 font-medium">Paste this into ElevenLabs, Murf, Play.ht, or any AI voice tool. Speak naturally, pause at paragraph breaks, and record screen alongside for a complete training video.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default function ProcessesMap() {
  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 sm:px-6 py-6 space-y-6">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-5 bg-gradient-to-r from-blue-50 via-white to-cyan-50">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                <Workflow className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-gray-900">Processes Map</h1>
                <p className="mt-1 text-sm text-gray-700">
                  Platform-admin operational playbook with process flows, click-by-click route guidance, and recording-ready narration text.
                </p>
              </div>
            </div>
          </div>

          <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-3 bg-white border-t border-gray-200">
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Layers className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">Process Families</span>
              </div>
              <p className="mt-1 text-sm text-gray-700">Onboarding, access governance, approvals, support, analytics, and automation.</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-3">
              <div className="flex items-center gap-2 text-blue-700">
                <PlayCircle className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">Recording Purpose</span>
              </div>
              <p className="mt-1 text-sm text-gray-700">Each process includes script paragraphs and exact click routes for guide videos.</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Shield className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">Audience</span>
              </div>
              <p className="mt-1 text-sm text-gray-700">Platform Admin / Super Admin operations across all companies.</p>
            </div>
          </div>
        </div>

        {/* Master end-to-end swimlane */}
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-blue-50">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                <Workflow className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">End-to-End Business Process — Cross-Role Swimlane</h2>
                <p className="mt-0.5 text-sm text-gray-600">
                  How all roles interact across the full operational cycle: company setup → budget planning → invoice &amp; expense processing → approvals → posting → analytics. Dashed arrows show cross-lane handoffs.
                </p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <MasterSwimlane />
          </div>
        </section>

        <section className="space-y-4">
          {processCards.map((process) => (
            <ProcessCard key={process.id} process={process} />
          ))}
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
          <h2 className="text-lg font-bold text-gray-900">Small But Important Processes</h2>
          <p className="mt-1 text-sm text-gray-700">
            These are quick operational flows that are often skipped in onboarding videos but are essential for complete platform mastery.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {miniProcesses.map((item) => (
              <div key={item.path} className="rounded-lg border border-gray-200 bg-white px-3 py-3">
                <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                <p className="mt-1 text-xs text-gray-600">Route: {item.path}</p>
                <p className="mt-1 text-sm text-gray-700">{item.purpose}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-3">
            <p className="text-sm text-blue-800">
              Tip for video creation: record each process as one chapter, keep route names visible in the sidebar, and call out destination URLs verbally to help new admins build navigation memory.
            </p>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Need a direct jump? Open <Link className="text-blue-600 hover:underline" to="/companies">Companies</Link>,
            <span> </span><Link className="text-blue-600 hover:underline" to="/support/tickets">Support Tickets</Link>,
            <span> </span><Link className="text-blue-600 hover:underline" to="/audit-logs">Audit Logs</Link>,
            <span> </span>or <Link className="text-blue-600 hover:underline" to="/scheduler">Scheduler</Link>.
          </div>
        </section>
      </div>
    </div>
  );
}
